import React from 'react'
import PropTypes from 'prop-types'
import { RadioGroup, Box, Text, Price, Icon, Logo, Level } from '@deseretbook/react-ui'

/**
 * Renders a form field for selecting a subscription plan. Used as part of subscription signups.
 *
 * @returns {function} Component.
 */
const SubscriptionPlanFormField = ({ subscriptionForm, detailsDocument }) => {
  const subscriptionDetails = (detailsDocument.data && detailsDocument.data.subscriptionPlans) || []
  const { selectedEstimate = {} } = subscriptionForm
  const { availableMembershipDiscounts = [] } = selectedEstimate.subscriptionPlan || {}

  const appliedMembershipDiscount = availableMembershipDiscounts
    .find(membershipDiscount => (
      selectedEstimate && selectedEstimate.discounts
        .some(appliedDiscount => appliedDiscount.code === membershipDiscount.code)
    ))

  return (
    <RadioGroup
      onChange={subscriptionForm.onFormChange}
      name="btPlanId"
      selectedValue={subscriptionForm.form.btPlanId}
      aria-label="Subscription Plan"
      disabled={subscriptionForm.isLoading}
    >
      {subscriptionForm.plans.map((plan) => {
        const displayDetails = subscriptionDetails
          .find(({ planId }) => planId === plan.btPlanId) || {}

        return (
          <RadioGroup.Option
            key={plan.btPlanId}
            value={plan.btPlanId}
          >
            <Box isColumn>
              <Level>
                <Level.Left>
                  <Level.Item>
                    <Text isBold hasEllipsis>{plan.name}</Text>
                  </Level.Item>
                  {displayDetails.callout && (
                    <Level.Item>
                      <Level>
                        <Level.Left>
                          <Level.Item>
                            <Icon name="star" size="small" color="primary" />
                          </Level.Item>
                          <Level.Item>
                            <Text
                              color="primary"
                              size="small"
                              hasEllipsis
                              style={{ textTransform: 'uppercase', letterSpacing: '1px' }}
                            >
                              {displayDetails.callout}
                            </Text>
                          </Level.Item>
                        </Level.Left>
                      </Level>
                    </Level.Item>
                  )}
                </Level.Left>
              </Level>
              <Box alignItems="baseline" isWrapped>
                {!appliedMembershipDiscount && (
                  <Box>
                    <Text hasEllipsis>
                      <Price value={plan.price} />
                      <Text size="small">
                        {
                          plan.yearly
                            ? '/year'
                            : '/month'
                        }
                      </Text>
                    </Text>
                  </Box>
                )}
                {plan.availableMembershipDiscounts
                  .filter(discount => (
                    !appliedMembershipDiscount
                      || discount.planSourceType === appliedMembershipDiscount.planSourceType
                  ))
                  .map(discount => (
                    <React.Fragment key={discount.code}>
                      {!appliedMembershipDiscount && (
                        <Box paddingHorizontal={1}>
                          <Text size="small">or</Text>
                        </Box>
                      )}
                      <Box>
                        <Text hasEllipsis>
                          <Price value={plan.price - discount.amount} />
                          <Text size="small">
                            {
                              plan.yearly
                                ? '/year with'
                                : '/month with'
                            }
                          </Text>
                          {discount.sourcePlanType === 'SubscriptionPlan::Platinum'
                            ? (
                              <Box width={8} height={1} paddingLeft={0.25}>
                                <Logo name="platinumRewards" />
                              </Box>
                            )
                            : <Text hasEllipsis>{discount.name}</Text>
                          }
                        </Text>
                      </Box>
                    </React.Fragment>
                  ))}
              </Box>
            </Box>
          </RadioGroup.Option>
        )
      })}
    </RadioGroup>
  )
}

SubscriptionPlanFormField.propTypes = {
  subscriptionForm: PropTypes.shape({
    plans: PropTypes.array,
  }).isRequired,
  detailsDocument: PropTypes.shape({}),
}

SubscriptionPlanFormField.defaultProps = {
  detailsDocument: {},
}

export default SubscriptionPlanFormField
